import { push } from 'react-router-redux'
import { arrayOf } from 'normalizr'
import uniq from 'lodash.uniq'
import mapValues from 'lodash.mapvalues'

import { fetch, schemas } from '../utils/fetch'
import { ENDPOINT_PLAYLISTS, ENDPOINT_PLAYLIST, ENDPOINT_NEW_PLAYLIST } from '../constants'

const PLAYLISTS_REQUEST = 'blayzr/playlists/REQUEST'
const PLAYLISTS_SUCCESS = 'blayzr/playlists/SUCCESS'
const PLAYLISTS_FAILURE = 'blayzr/playlists/FAIL'

const SAVE_PLAYLIST_REQUEST = 'blayzr/playlists/SAVE_REQUEST'
const SAVE_PLAYLIST_SUCCESS = 'blayzr/playlists/SAVE_SUCCESS'
const SAVE_PLAYLIST_FAILURE = 'blayzr/playlists/SAVE_FAIL'

const DELETE_PLAYLIST = 'blayzr/playlists/DELETE'
const REORDER_LISTS = 'blayzr/playlists/REORDER_LISTS'

const initialState = {
  playlists: null,
  lists: null,
  fetching: false,
  error: null,
  submitting: false,
  submitError: null
}

export default function reducer (state = initialState, action = {}) {
  const { type, payload, meta } = action
  switch (type) {
    case PLAYLISTS_REQUEST:
      return {
        ...state,
        fetching: true,
        error: null
      }
    case PLAYLISTS_SUCCESS:
      const sortPlaylists = (a, b) => {
        const playlistA = payload.entities.playlists[a]
        const playlistB = payload.entities.playlists[b]
        return new Date(playlistA.created_at) > new Date(playlistB.created_at) ? -1 : 1
      }
      const playlists = payload.result.records
        .filter(id => payload.entities.playlists[id].playlist)
        .sort(sortPlaylists)
      const lists = payload.result.records
        .filter(id => !payload.entities.playlists[id].playlist)
        .sort((a, b) => {
          const playlistsOnlyA = /"playlistsOnly":true/.test(payload.entities.playlists[a].json_data)
          const playlistsOnlyB = /"playlistsOnly":true/.test(payload.entities.playlists[b].json_data)
          if (playlistsOnlyA && !playlistsOnlyB) return 1
          if (!playlistsOnlyA && playlistsOnlyB) return -1
          return 0
        })
      return {
        ...state,
        fetching: false,
        playlists: state.playlists ? uniq(state.playlists.concat(playlists)) : playlists,
        lists: state.lists ? uniq(state.lists.concat(lists)) : lists
      }
    case PLAYLISTS_FAILURE:
      return {
        ...state,
        fetching: false,
        error: payload.error
      }
    case SAVE_PLAYLIST_REQUEST:
      return {
        ...state,
        submitting: true,
        submitError: false
      }
    case SAVE_PLAYLIST_SUCCESS:
      return {
        ...state,
        submitting: false,
        playlists: state.playlists && meta.playlist ? uniq([meta.id].concat(state.playlists)) : state.playlists,
        lists: state.lists && !meta.playlist ? uniq([meta.id].concat(state.lists)) : state.lists
      }
    case SAVE_PLAYLIST_FAILURE:
      return {
        ...state,
        submitting: false,
        submitError: payload
      }
    case DELETE_PLAYLIST:
      if (state.playlists) {
        return {
          ...state,
          playlists: state.playlists.filter(id => id !== meta.id),
          lists: state.lists.filter(id => id !== meta.id)
        }
      }
      return state
    case REORDER_LISTS:
      return {
        ...state,
        lists: payload
      }
    default:
      return state
  }
}

export function playlistsRequest () {
  return {
    type: PLAYLISTS_REQUEST
  }
}

export function playlistsSuccess (data) {
  return {
    type: PLAYLISTS_SUCCESS,
    payload: data
  }
}

export function playlistsFailure (error) {
  return {
    type: PLAYLISTS_FAILURE,
    payload: { error }
  }
}

export function savePlaylistRequest () {
  return {
    type: SAVE_PLAYLIST_REQUEST
  }
}

export function savePlaylistSuccess (id, data) {
  const playlist = data.entities.playlists[id]
  return {
    type: SAVE_PLAYLIST_SUCCESS,
    payload: { entities: { playlists: {
      [id]: playlist
    }}},
    meta: {
      id,
      playlist: playlist.playlist
    }
  }
}

export function savePlaylistFailure (error) {
  return {
    type: SAVE_PLAYLIST_FAILURE,
    payload: error
  }
}

export function fetchPlaylists () {
  return (dispatch, getState) => {
    const options = {
      token: getState().auth.token,
      schema: {
        records: arrayOf(schemas.playlist)
      }
    }
    dispatch(playlistsRequest())
    return fetch(ENDPOINT_PLAYLISTS, options, dispatch).then(data => {
      dispatch(playlistsSuccess(data))
    }, e => {
      dispatch(playlistsFailure(e))
    })
  }
}

export function saveList (data, structure, id, redirect = '/playlists/:id') {
  return (dispatch, getState) => {
    const endpoint = id ? ENDPOINT_PLAYLIST.replace(':id', id) : ENDPOINT_NEW_PLAYLIST
    const options = {
      method: 'post',
      token: getState().auth.token,
      schema: {
        records: schemas.playlist
      },
      body: {
        ...data,
        albums: undefined, // Albums property is added by mapStateToLists util, so remove here
        json_data: JSON.stringify(structure)
      }
    }
    dispatch(savePlaylistRequest())
    return fetch(endpoint, options, dispatch).then(data => {
      const id = data.result.records
      dispatch(savePlaylistSuccess(id, data))
      if (redirect) {
        dispatch(push(redirect.replace(':id', id)))
      }
      return data.entities.playlists[id]
    }, e => {
      dispatch(savePlaylistFailure(e))
    })
  }
}

export function savePlaylist (data, ...args) {
  return saveList({ ...data, playlist: true }, ...args)
}

export function deletePlaylist (id) {
  return (dispatch, getState) => {
    const state = getState()
    const endpoint = ENDPOINT_PLAYLIST.replace(':id', id)
    fetch(endpoint, {
      method: 'delete',
      token: state.auth.token
    })
    dispatch({
      type: DELETE_PLAYLIST,
      meta: { id },
      payload: {
        entities: {
          playlists: {
            [id]: undefined
          },
          // Remove playlist ID from any queues
          queues: mapValues(state.entities.queues || {}, queue => {
            const data = JSON.parse(queue.json_data)
            return {
              ...queue,
              json_data: JSON.stringify({
                ...data,
                playlists: data.playlists.filter(playlistID => playlistID !== id)
              })
            }
          })
        }
      }
    })
    dispatch(push('/playlists'))
  }
}

export function addAlbumToList (albumID, list) {
  const structure = JSON.parse(list.json_data)
  if (structure.albums.map(a => a.album).indexOf(albumID) === -1) {
    structure.albums.push({ album: albumID })
  }
  return saveList(list, structure, list.id, null)
}

export function removeAlbumFromList (albumID, list) {
  const structure = JSON.parse(list.json_data)
  structure.albums = structure.albums.filter(a => a.album !== albumID)
  return saveList(list, structure, list.id, null)
}

export function updateListOrder (ids) {
  return (dispatch, getState) => {
    fetch(ENDPOINT_PLAYLISTS, {
      method: 'put',
      token: getState().auth.token,
      body: { sort: ids }
    })
    dispatch({
      type: REORDER_LISTS,
      payload: ids
    })
  }
}



// WEBPACK FOOTER //
// ./src/redux/playlists.js